import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';
import { Plus, Trash2, AlertOctagon, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REASONS = [
  { value: 'vencido', label: 'Vencido', color: '#dc2626' },
  { value: 'avariado', label: 'Avariado / Quebrado', color: '#ea580c' },
  { value: 'furto', label: 'Furto / Perda', color: '#7c3aed' },
  { value: 'derramamento', label: 'Derramamento', color: '#0891b2' },
  { value: 'amostra', label: 'Amostra / Degustação', color: '#16a34a' },
  { value: 'outros', label: 'Outros', color: '#64748b' },
];

const REASON_COLORS = REASONS.reduce((acc, r) => ({ ...acc, [r.value]: r.color }), {});
const REASON_LABELS = REASONS.reduce((acc, r) => ({ ...acc, [r.value]: r.label }), {});

export const LossesContent = () => {
  const [losses, setLosses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [days, setDays] = useState(30);
  const [filterReason, setFilterReason] = useState('all');
  const [productSearch, setProductSearch] = useState('');
  const [formData, setFormData] = useState({
    product_id: '', quantity: '', reason: 'vencido', notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadProducts(); }, []);
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [days, filterReason]);

  const loadProducts = async () => {
    try {
      const { data } = await axios.get(`${API}/products`, { withCredentials: true });
      setProducts(data);
    } catch (err) { /* ignore */ }
  };

  const loadAll = async () => {
    try {
      const reasonParam = filterReason !== 'all' ? `&reason=${filterReason}` : '';
      const [lRes, sRes] = await Promise.all([
        axios.get(`${API}/losses?days=${days}${reasonParam}`, { withCredentials: true }),
        axios.get(`${API}/losses/summary?days=${days}`, { withCredentials: true }),
      ]);
      setLosses(lRes.data);
      setSummary(sRes.data);
    } catch (err) {
      setError('Erro ao carregar perdas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.product_id || !formData.quantity) {
      setError('Produto e quantidade são obrigatórios');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/losses`, {
        product_id: formData.product_id,
        quantity: parseFloat(formData.quantity),
        reason: formData.reason,
        notes: formData.notes || null,
      }, { withCredentials: true });
      setFormData({ product_id: '', quantity: '', reason: 'vencido', notes: '' });
      setShowForm(false);
      setProductSearch('');
      loadAll();
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao registrar perda');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Reverter esta perda? O estoque será devolvido.')) return;
    try {
      await axios.delete(`${API}/losses/${id}`, { withCredentials: true });
      loadAll();
      loadProducts();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao reverter');
    }
  };

  const filteredProducts = products.filter(p =>
    p.stock_quantity > 0 &&
    (!productSearch || p.name.toLowerCase().includes(productSearch.toLowerCase()) || (p.code && p.code.includes(productSearch)))
  ).slice(0, 50);

  const selectedProduct = products.find(p => p.id === formData.product_id);
  const estimatedCost = selectedProduct && formData.quantity
    ? (parseFloat(formData.quantity) || 0) * (selectedProduct.cost_price || 0)
    : 0;

  const pieData = (summary?.by_reason || []).map(r => ({
    name: REASON_LABELS[r.reason] || r.reason,
    value: r.total,
    raw: r.reason,
  }));

  return (
    <div data-testid="losses-section">
      {/* Filtros + ação */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Label className="text-xs uppercase tracking-wider text-slate-600">Período:</Label>
          {[7, 30, 90, 180].map(d => (
            <Button
              key={d}
              size="sm"
              variant={days === d ? 'default' : 'outline'}
              onClick={() => setDays(d)}
              className={days === d ? 'bg-red-600 hover:bg-red-700 h-8' : 'h-8'}
              data-testid={`losses-period-${d}`}
            >
              {d}d
            </Button>
          ))}
          <Select value={filterReason} onValueChange={setFilterReason}>
            <SelectTrigger className="h-8 w-44 ml-2" data-testid="losses-filter-reason">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os motivos</SelectItem>
              {REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowForm(!showForm)} className="bg-red-600 hover:bg-red-700" data-testid="losses-add-btn">
          <Plus size={16} className="mr-1" /> Registrar Perda
        </Button>
      </div>

      {error && <Alert variant="destructive" className="mb-3"><AlertDescription>{error}</AlertDescription></Alert>}

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
          <div className="bg-red-50 border border-red-200 rounded-md p-3">
            <p className="text-xs uppercase tracking-wider text-red-700 font-bold">Prejuízo total</p>
            <p className="text-2xl font-black text-red-700" data-testid="losses-kpi-total">R$ {summary.total_cost_impact.toFixed(2)}</p>
            <p className="text-[10px] text-red-600">{summary.total_count} registro(s)</p>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
            <p className="text-xs uppercase tracking-wider text-amber-700 font-bold">Quantidade perdida</p>
            <p className="text-2xl font-black text-amber-700">{summary.total_quantity.toFixed(3)}</p>
            <p className="text-[10px] text-amber-600">unidades / kg</p>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <p className="text-xs uppercase tracking-wider text-blue-700 font-bold">Categorias afetadas</p>
            <p className="text-2xl font-black text-blue-700">{summary.by_category.length}</p>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3">
            <p className="text-xs uppercase tracking-wider text-slate-700 font-bold">Motivos</p>
            <p className="text-2xl font-black text-slate-700">{summary.by_reason.length}</p>
          </div>
        </div>
      )}

      {/* Formulário */}
      {showForm && (
        <div className="bg-white rounded-md border border-slate-200 p-4 mb-5">
          <h3 className="text-lg font-bold mb-3 flex items-center gap-2">
            <AlertOctagon size={18} className="text-red-600" /> Nova Quebra / Perda
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Buscar produto</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                <Input
                  value={productSearch}
                  onChange={(e) => setProductSearch(e.target.value)}
                  placeholder="Nome ou código..."
                  className="pl-9"
                  data-testid="losses-product-search"
                />
              </div>
              {productSearch && (
                <div className="border border-slate-200 rounded mt-1 max-h-48 overflow-y-auto">
                  {filteredProducts.length === 0 ? (
                    <p className="p-3 text-sm text-slate-500">Nenhum produto com estoque encontrado</p>
                  ) : filteredProducts.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => { setFormData({ ...formData, product_id: p.id }); setProductSearch(p.name); }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-50 ${formData.product_id === p.id ? 'bg-emerald-50' : ''}`}
                      data-testid={`losses-product-${p.id}`}
                    >
                      <span className="font-medium">{p.name}</span>
                      <span className="text-xs text-slate-500 ml-2">Estoque: {p.stock_quantity} {p.unit} · Custo: R$ {(p.cost_price || 0).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label>Quantidade *</Label>
              <Input
                type="number" step="0.01" min="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                required
                data-testid="losses-quantity"
              />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Select value={formData.reason} onValueChange={(v) => setFormData({ ...formData, reason: v })}>
                <SelectTrigger data-testid="losses-reason"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {REASONS.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label>Observações</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Detalhes (opcional)"
              />
            </div>
            {selectedProduct && formData.quantity && (
              <div className="md:col-span-2 bg-red-50 border border-red-200 rounded p-3 flex items-center justify-between">
                <span className="text-sm text-slate-700">
                  Prejuízo estimado para <strong>{selectedProduct.name}</strong>:
                </span>
                <span className="text-xl font-black text-red-700">R$ {estimatedCost.toFixed(2)}</span>
              </div>
            )}
            <div className="md:col-span-2 flex gap-2 mt-2">
              <Button type="submit" disabled={loading} className="bg-red-600 hover:bg-red-700" data-testid="losses-submit">
                {loading ? 'Salvando...' : 'Registrar Perda'}
              </Button>
              <Button type="button" variant="outline" onClick={() => { setShowForm(false); setProductSearch(''); setFormData({ product_id: '', quantity: '', reason: 'vencido', notes: '' }); }}>
                Cancelar
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Gráficos */}
      {summary && summary.total_count > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-2">Por motivo</p>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(d) => `R$ ${d.value.toFixed(0)}`}>
                  {pieData.map((d, i) => <Cell key={i} fill={REASON_COLORS[d.raw] || '#64748b'} />)}
                </Pie>
                <Tooltip formatter={(v) => `R$ ${Number(v).toFixed(2)}`} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-3">
            <p className="text-xs uppercase tracking-wider font-bold text-slate-700 mb-2">Top produtos com perdas</p>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={summary.top_products.slice(0, 8)} layout="vertical" margin={{ top: 5, right: 10, left: 60, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={90} />
                <Tooltip formatter={(v) => `R$ ${Number(v).toFixed(2)}`} />
                <Bar dataKey="cost" fill="#dc2626" radius={[0, 3, 3, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Tabela */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600">
            <tr>
              <th className="text-left p-2">Data</th>
              <th className="text-left p-2">Produto</th>
              <th className="text-left p-2">Motivo</th>
              <th className="text-right p-2">Qtd</th>
              <th className="text-right p-2">Prejuízo</th>
              <th className="text-left p-2">Operador</th>
              <th className="text-left p-2">Obs.</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {losses.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-center text-slate-400">Nenhuma perda registrada no período</td></tr>
            ) : losses.map(l => (
              <tr key={l.id} className="hover:bg-red-50/40" data-testid={`loss-row-${l.id}`}>
                <td className="p-2 text-xs">{new Date(l.created_at).toLocaleString('pt-BR')}</td>
                <td className="p-2 font-medium">{l.product_name}</td>
                <td className="p-2">
                  <span className="px-2 py-0.5 rounded text-xs font-medium" style={{ backgroundColor: (REASON_COLORS[l.reason] || '#64748b') + '20', color: REASON_COLORS[l.reason] || '#64748b' }}>
                    {REASON_LABELS[l.reason] || l.reason}
                  </span>
                </td>
                <td className="p-2 text-right">{l.quantity} {l.unit}</td>
                <td className="p-2 text-right font-bold text-red-700">R$ {Number(l.cost_impact).toFixed(2)}</td>
                <td className="p-2 text-xs text-slate-600">{l.user_name}</td>
                <td className="p-2 text-xs text-slate-500">{l.notes || '—'}</td>
                <td className="p-2 text-right">
                  <button onClick={() => handleDelete(l.id)} className="text-slate-400 hover:text-red-600" title="Reverter (admin)" data-testid={`loss-delete-${l.id}`}>
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LossesContent;
